// Outbound Loads — cargas de salida agrupadas por fecha.
//
// El eje de la pantalla es la PROXIMIDAD A HOY (`agruparPorFecha`), no una
// tabla plana: con ~660 cargas vivas, lo que hay que ver primero es qué va
// tarde, no un listado ordenable por columna. Por eso no hay `Pagination` ni
// cabeceras de columna ordenables — pelearían con la agrupación.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { casaConHueco, opcionesConHueco, SIN_INFORMAR } from '@/utils/filtroHuecos'
import type { OpcionMulti } from '@/components/common/MultiFiltro'
import { useFiltrosPersistentes } from '@/hooks/useFiltrosPersistentes'
import { useCargasSalida, useArticulosCatalogo } from '@/hooks/useCargasSalida'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useMarcasFilas, BarraMarcas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { agruparPorFecha } from '@/utils/agruparPorFecha'
import { predicadoTextoAgregado } from '@/utils/filtroTexto'
import {
  articulosDe, descripcionesDe, enRangoFecha, opcionesArticuloCascada, estadoPickingDe, fechaDe, incidenciasDe, modoTransporteDe, nivelDe, NIVELES_ORDEN, reservaDe, retrasoDe,
} from '@/utils/cargasSalida'
import type { NivelCarga } from '@/utils/cargasSalida'
import { cargasSalidaApi } from '@/services/api'
import { exportCargasSalidaExcel } from '@/utils/exportCargasSalidaExcel'
import { AvisoExport, errorExportDe, type ErrorExport } from '@/components/common/AvisoExport'
import { PageHeader } from '@/components/common/PageHeader'
import { RefreshButton } from '@/components/common/RefreshButton'
import {
  CargasSalidaFiltros, EMPTY_FILTROS, contarActivos,
  type CargasSalidaFiltrosState,
} from '@/components/cargas-salida/CargasSalidaFiltros'
import { CargasSalidaKpis } from '@/components/cargas-salida/CargasSalidaKpis'
import { CargasSalidaTabla } from '@/components/cargas-salida/CargasSalidaTabla'
import { columnasBase } from '@/components/cargas-salida/columnasCargaSalida'
import { filtrosDeColumna } from '@/components/cargas-salida/filtrosDeColumna'
import type { CargaSalida } from '@/types'
import { useVariasEmpresas } from '@/hooks/useVariasEmpresas'


/** Los filtros que tienen catálogo propio y, por tanto, cascada. */
type CampoFiltro =
  | 'carga' | 'pedido' | 'cliente' | 'articulo'
  | 'almacen' | 'empresa' | 'grupo' | 'incoterm' | 'modo'
  | 'picking' | 'nivel' | 'reserva' | 'paletizada' | 'muelle'

/** Una lista de la URL: los términos van separados por «|», que no aparece en
 *  ningún nº de carga, pedido ni código de cliente. */
function lista(bruto: string | null): string[] {
  return (bruto ?? '').split('|').filter(Boolean)
}

/** La reserva que venga en la URL, si sigue existiendo.
 *
 *  «incompleta» era el valor de antes y mezclaba «sin reserva» con «parcial».
 *  Un enlace guardado con él cae a «todas»: la alternativa sería dejar la
 *  pantalla en blanco sin que nada explique por qué. */
function reservaValida(bruto: string | null): CargasSalidaFiltrosState['reserva'] {
  return bruto === 'sin' || bruto === 'parcial' || bruto === 'completa' ? bruto : ''
}

export default function CargasSalidaPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const isEmpresaAll = useIsEmpresaAll()
  // El filtro de empresa se ensena si hay MAS DE UNA que elegir, no si el rol
  // es `Empresa.All`: con AVI+TDIS habia dos empresas y ningun desplegable.
  const variasEmpresas = useVariasEmpresas()
  const [searchParams, setSearchParams] = useSearchParams()

  const { paramsIniciales: params0, olvidar } = useFiltrosPersistentes('cargasSalida')

  const [filtros, setFiltros] = useState<CargasSalidaFiltrosState>({
    ...EMPTY_FILTROS,
    carga: lista(params0.get('c')),
    pedido: lista(params0.get('p')),
    cliente: lista(params0.get('cli')),
    articulo: lista(params0.get('art')),
    // `q` es la clave del buscador libre de antes. Se sigue leyendo para que un
    // enlace guardado filtre lo que filtraba entonces; los campos nuevos
    // estrenan claves propias y no se pisan con ella.
    terminos: lista(params0.get('q')),
    desde: params0.get('desde') ?? '',
    hasta: params0.get('hasta') ?? '',
    almacen: lista(params0.get('almacen')),
    empresa: lista(params0.get('emp')),
    grupo: lista(params0.get('grupo')),
    incoterm: lista(params0.get('inco')),
    modo: lista(params0.get('mod')),
    estadoPicking: lista(params0.get('picking')) as CargasSalidaFiltrosState['estadoPicking'],
    nivel: lista(params0.get('nivel')) as NivelCarga[],
    // «incompleta» ya no existe: mezclaba «sin reserva» con «parcial». Un enlace
    // antiguo cae a «todas» en vez de dejar la pantalla en blanco.
    reserva: reservaValida(params0.get('reserva')),
    paletizada: lista(params0.get('pal')) as CargasSalidaFiltrosState['paletizada'],
    soloAereas: params0.get('aerea') === '1',
    muelle: lista(params0.get('muelle')) as CargasSalidaFiltrosState['muelle'],
    soloAtrasadas: params0.get('atrasadas') === '1',
    soloIncidencia: params0.get('incidencia') === '1',
    incluirExpedidas: params0.get('expedidas') === '1',
  })

  // Sólo `incluirExpedidas` es parámetro de la petición: el resto se filtra en
  // cliente sobre el conjunto que ya trae el backend (vivas, ≤30 días).
  const { cargas, isLoading, isError, isFetching, refetch } = useCargasSalida({
    incluirExpedidas: filtros.incluirExpedidas,
  })

  const catalogoArticulosApi = useArticulosCatalogo()
  const [exporting, setExporting] = useState(false)
  const [errorExport, setErrorExport] = useState<ErrorExport | null>(null)

  useEffect(() => {
    const p = new URLSearchParams()
    const poner = (clave: string, valor: string) => { if (valor) p.set(clave, valor) }
    poner('c', filtros.carga.join('|'))
    poner('p', filtros.pedido.join('|'))
    poner('cli', filtros.cliente.join('|'))
    poner('art', filtros.articulo.join('|'))
    poner('q', filtros.terminos.join('|'))
    poner('desde', filtros.desde)
    poner('hasta', filtros.hasta)
    poner('almacen', filtros.almacen.join('|'))
    poner('emp', filtros.empresa.join('|'))
    poner('grupo', filtros.grupo.join('|'))
    poner('inco', filtros.incoterm.join('|'))
    poner('mod', filtros.modo.join('|'))
    poner('picking', filtros.estadoPicking.join('|'))
    poner('nivel', filtros.nivel.join('|'))
    poner('reserva', filtros.reserva)
    poner('pal', filtros.paletizada.join('|'))
    if (filtros.soloAereas) p.set('aerea', '1')
    poner('muelle', filtros.muelle.join('|'))
    if (filtros.soloAtrasadas) p.set('atrasadas', '1')
    if (filtros.soloIncidencia) p.set('incidencia', '1')
    if (filtros.incluirExpedidas) p.set('expedidas', '1')
    if (p.toString() !== searchParams.toString()) {
      setSearchParams(p, { replace: true })
    }
  }, [filtros, searchParams, setSearchParams])

  // Estable a propósito: lo consume el `useMemo` que arma los filtros de
  // cabecera, y redeclararlo en cada render lo invalidaría siempre. No cierra
  // sobre el estado —actualiza por función—, así que `[]` es correcto.
  const patchFiltros = useCallback((patch: Partial<CargasSalidaFiltrosState>) => {
    setFiltros(f => ({ ...f, ...patch }))
  }, [])
  function clearFilters() {
    setFiltros(EMPTY_FILTROS)
    olvidar()
  }

  // «Hoy» fijo para todo el render: agrupar y filtrar tienen que hablar del
  // mismo instante, o una carga podría caer en una banda y quedar fuera del
  // filtro «sólo atrasadas» que mira otro «ahora».
  const hoy = useMemo(() => new Date(), [])

  // Predicado de TODOS los filtros.
  //
  // `excepto` deja uno fuera, y es lo que hace la CASCADA: el catálogo de un
  // desplegable se calcula con las cargas que pasan todos los filtros MENOS el
  // suyo. Así, al elegir una empresa, los demás desplegables se quedan sólo con
  // lo que esa empresa tiene — que es el comportamiento de siempre — y el
  // desplegable no se vacía a sí mismo en cuanto eliges una opción.
  //
  // Antes esto existía sólo para artículo. Al añadir los buscadores por campo
  // se quedaron fuera, y con ellos la cascada: ésa fue la regresión.
  const pasaFiltros = useMemo(() => {
    // Coincidencia EXACTA sobre el valor del catálogo, no por subcadena: por
    // subcadena, «QARF1» arrastraría también «QARF10» y el pedido «100» traería
    // el «1000».
    const sel = (xs: string[]) => new Set(xs)
    const cargas_ = sel(filtros.carga)
    const pedidos_ = sel(filtros.pedido)
    const clientes_ = sel(filtros.cliente)
    const articulos_ = sel(filtros.articulo)
    const picking_ = sel(filtros.estadoPicking)
    const niveles_ = sel(filtros.nivel)
    const almacenes_ = sel(filtros.almacen)
    const empresas_ = sel(filtros.empresa)
    const paletizada_ = sel(filtros.paletizada)
    const muelles_ = sel(filtros.muelle)
    // El buscador antiguo sigue haciendo lo de siempre sobre el amasijo, para
    // los enlaces que aún lo traen. Es la única parte que conserva el defecto, y
    // a propósito: cambiarle el significado a un enlace ya repartido es peor.
    const casaLegado = predicadoTextoAgregado(filtros.terminos)
    return (c: CargaSalida, excepto?: CampoFiltro): boolean => {
      if (excepto !== 'articulo' && articulos_.size > 0 &&
          !articulosDe(c).some(a => articulos_.has(a))) return false
      if (excepto !== 'carga' && cargas_.size > 0 && !cargas_.has(c.carga)) return false
      // Una carga lleva varios pedidos: casa si alguno de los suyos está pedido.
      if (excepto !== 'pedido' && pedidos_.size > 0 &&
          !c.pedidos.some(p => pedidos_.has(p))) return false
      if (excepto !== 'cliente' && clientes_.size > 0 &&
          !clientes_.has(c.cliente ?? '')) return false
      if (!enRangoFecha(c, filtros.desde, filtros.hasta)) return false
      if (excepto !== 'almacen' && almacenes_.size > 0 &&
          !almacenes_.has(c.almacen ?? '')) return false
      if (excepto !== 'empresa' && empresas_.size > 0 && !empresas_.has(c.empresa)) return false
      if (excepto !== 'grupo' && !casaConHueco(c.grupoCliente, filtros.grupo)) return false
      if (excepto !== 'incoterm' && !casaConHueco(c.incoterm, filtros.incoterm)) return false
      // Por el modo YA RESUELTO, que es lo que la columna pinta: filtrar
      // por el campo crudo dejaria fuera 195 de las 227 cargas aereas.
      if (excepto !== 'modo' && !casaConHueco(modoTransporteDe(c), filtros.modo)) return false
      if (excepto !== 'picking' && picking_.size > 0 &&
          !picking_.has(estadoPickingDe(c))) return false
      // El semáforo de preparación, que es la chapa de la columna «Estado».
      // No es lo mismo que `estadoPicking`: aquél mira sólo el picking y éste
      // además la reserva y si la carga ya salió del almacén.
      if (excepto !== 'nivel' && niveles_.size > 0 && !niveles_.has(nivelDe(c))) return false
      if (excepto !== 'reserva' && filtros.reserva &&
          reservaDe(c).estado !== filtros.reserva) return false
      if (excepto !== 'paletizada' && paletizada_.size > 0) {
        const suyo = c.paletizada === true ? 'si' : c.paletizada === false ? 'no' : 'noConsta'
        if (!paletizada_.has(suyo)) return false
      }
      if (filtros.soloAereas && !c.aerea) return false
      if (excepto !== 'muelle' && muelles_.size > 0 &&
          !muelles_.has(c.muelle ? 'con' : 'sin')) return false
      if (filtros.soloAtrasadas && retrasoDe(c, hoy) == null) return false
      if (filtros.soloIncidencia && incidenciasDe(c, hoy).length === 0) return false
      if (!casaLegado(`${c.carga} ${c.pedidos.join(' ')} ${c.cliente ?? ''}`)) return false
      return true
    }
  }, [filtros, hoy])

  const filtered = useMemo(() => cargas.filter(c => pasaFiltros(c)), [cargas, pasaFiltros])

  const fmtArt = useFmtArticulo()
  const descripciones = useMemo(() => descripcionesDe(catalogoArticulosApi), [catalogoArticulosApi])
  const sinInformar = t('common.filtros.sinInformar')

  // Todos los catálogos en cascada: cada uno ve las cargas que pasan los demás
  // filtros, no las que pasan el suyo. El endpoint de catálogo sólo pone la
  // descripción del artículo (sin ella, la opción se enseña igual).
  const enCascada = useCallback(
    (excepto: CampoFiltro) => cargas.filter(c => pasaFiltros(c, excepto)),
    [cargas, pasaFiltros],
  )

  const opcionesArticulo = useMemo(
    () => opcionesArticuloCascada(enCascada('articulo'), descripciones, fmtArt),
    [enCascada, descripciones, fmtArt],
  )
  const opcionesCarga = useMemo<OpcionMulti[]>(
    () => [...new Set(enCascada('carga').map(c => c.carga))].sort()
      .map(valor => ({ valor, titulo: valor })),
    [enCascada],
  )
  const opcionesPedido = useMemo<OpcionMulti[]>(
    () => [...new Set(enCascada('pedido').flatMap(c => c.pedidos))].sort()
      .map(valor => ({ valor, titulo: valor })),
    [enCascada],
  )
  // Por NOMBRE, que es lo que la tabla pinta; el código queda de detalle y
  // `MultiFiltro` también busca en él, así que pegarlo sigue funcionando.
  const opcionesCliente = useMemo<OpcionMulti[]>(() => {
    const porCodigo = new Map<string, string>()
    for (const c of enCascada('cliente')) {
      const codigo = c.cliente ?? ''
      if (codigo && !porCodigo.has(codigo)) porCodigo.set(codigo, c.clienteNombre ?? codigo)
    }
    return [...porCodigo.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([valor, titulo]) => ({ valor, titulo, detalle: valor }))
  }, [enCascada])
  const opcionesGrupo = useMemo(
    () => opcionesConHueco(enCascada('grupo').map(c => c.grupoCliente), sinInformar),
    [enCascada, sinInformar],
  )
  // Las opciones salen de las cargas, no de una lista fija: un modo nuevo del
  // notebook aparece solo, y uno que ya no se usa deja de ofrecerse. Los
  // titulos se traducen aqui porque `opcionesConHueco` devuelve el valor crudo.
  const opcionesModo = useMemo(
    () => opcionesConHueco(enCascada('modo').map(modoTransporteDe), sinInformar)
      .map(o => (o.valor === SIN_INFORMAR
        ? o
        : { ...o, titulo: t(`cargasSalida.tabla.modos.${o.valor}`, { defaultValue: o.valor }) })),
    [enCascada, sinInformar, t],
  )
  // En cascada y en orden de AVANCE, no alfabético. `expedida` sólo aparece
  // cuando hay cargas expedidas en lo cargado: ofrecerla siempre daría una
  // opción que no selecciona nada, que es otra forma de mentir.
  const opcionesNivel = useMemo<OpcionMulti[]>(() => {
    const presentes = new Set(enCascada('nivel').map(nivelDe))
    return NIVELES_ORDEN.filter(n => presentes.has(n))
      .map(valor => ({ valor, titulo: t(`cargasSalida.nivel.${valor}`) }))
  }, [enCascada, t])
  const opcionesIncoterm = useMemo(
    () => opcionesConHueco(enCascada('incoterm').map(c => c.incoterm), sinInformar),
    [enCascada, sinInformar],
  )
  const almacenes = useMemo(
    () => [...new Set(enCascada('almacen').map(c => c.almacen).filter((a): a is string => !!a))].sort(),
    [enCascada],
  )
  // Las empresas salen del ROL, no de los datos: que no haya cargas de una
  // empresa hoy no significa que el usuario no pueda preguntar por ella.
  const empresasFiltro = useMemo(
    () => [...new Set(enCascada('empresa').map(c => c.empresa))].sort(),
    [enCascada],
  )

  const grupos = useMemo(
    () => agruparPorFecha(filtered, c => fechaDe(c.fechaEnvioPrevista), {
      hoy, clave: c => c.carga,
    }),
    [filtered, hoy],
  )

  const columnas = useMemo(() => columnasBase(), [])

  // Lo que ofrece filtrar cada columna. Los catálogos son los mismos que antes
  // alimentaban la barra —los de la cascada—, sólo que ahora cuelgan de su
  // cabecera. Artículo y empresa NO están aquí: no tienen columna.
  const filtrosColumna = useMemo(
    () => filtrosDeColumna(filtros, patchFiltros, {
      carga: opcionesCarga,
      pedido: opcionesPedido,
      cliente: opcionesCliente,
      almacenes,
      grupo: opcionesGrupo,
      incoterm: opcionesIncoterm,
      modo: opcionesModo,
      nivel: opcionesNivel,
    }, t),
    [filtros, patchFiltros, opcionesCarga, opcionesPedido, opcionesCliente,
     almacenes, opcionesGrupo, opcionesIncoterm, opcionesModo, opcionesNivel, t],
  )
  const marcas = useMarcasFilas('cargasSalida')
  const claveDe = (c: CargaSalida) => claveFila(c.empresa, c.carga)

  const activeFilters = contarActivos(filtros)

  async function handleExport() {
    if (filtered.length === 0 || exporting) return
    setExporting(true)
    setErrorExport(null)
    try {
      // Las líneas se piden en UNA llamada con las cargas que hay en pantalla
      // (ya filtradas), no carga a carga: una por carga serían cientos de
      // peticiones para un solo botón.
      const res = await cargasSalidaApi.getLineasBulk(filtered.map(c => c.carga))
      await exportCargasSalidaExcel(filtered, { isEmpresaAll, t, lineas: res.data ?? [] })
    } catch (e) {
      setErrorExport(errorExportDe(e))
    } finally {
      setExporting(false)
    }
  }

  const pageBg = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>
      <PageHeader
        dark={dark}
        title={t('cargasSalida.title')}
        subtitle={
          <>
            {filtered.length} {t('common.of')} {cargas.length} {t('cargasSalida.subtitleUnit')}
            {activeFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                · {t('common.activeFilter', { count: activeFilters })}
              </span>
            )}
          </>
        }
        actions={
          <>
            {isError && <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>}
            <AvisoExport error={errorExport} dark={dark} />
            <RefreshButton dark={dark} onClick={refetch} isLoading={isFetching} />
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || filtered.length === 0}
              className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer disabled:opacity-40 ${
                dark
                  ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm'
              }`}
            >
              {exporting ? t('common.exporting') : t('common.exportExcel')}
            </button>
          </>
        }
      />

      <CargasSalidaKpis dark={dark} cargas={filtered} grupos={grupos} isLoading={isLoading} />

      <CargasSalidaFiltros
        dark={dark}
        value={filtros}
        onChange={patchFiltros}
        onClear={clearFilters}
        opcionesArticulo={opcionesArticulo}
        opcionesCliente={opcionesCliente}
        empresas={empresasFiltro}
        showEmpresa={variasEmpresas}
        resumen={`${filtered.length} ${t('cargasSalida.subtitleUnit')}`}
      />

      <BarraMarcas dark={dark} marcas={marcas} />

      <CargasSalidaTabla
        dark={dark}
        grupos={grupos}
        columnas={columnas}
        filtros={filtrosColumna}
        hoy={hoy}
        marcas={marcas}
        claveDe={claveDe}
        isLoading={isLoading}
        isError={isError}
        hasFilters={activeFilters > 0}
        onClearFilters={clearFilters}
        onRetry={refetch}
      />
    </div>
  )
}
